import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import {
  AlertCircle,
  Check,
  Download,
  Filter,
  LoaderCircle,
  Save,
  Share2,
  Tags,
  Trash2,
  UserX,
  Users,
  X,
} from "lucide-react";
import { useDebounce } from "@/hooks/use-debounce";
import { useContactList, useContactSources, createContact, exportAllContactsCsv, type ContactSummary } from "@/hooks/use-contacts";
import { useOrganizationMembers } from "@/hooks/use-organization-members";
import {
  createSavedView,
  deleteSavedView,
  useSavedViews,
  type SavedViewFilter,
  type SavedViewRecord,
} from "@/hooks/use-saved-views";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/skeleton";
import { apiFetch, ApiError } from "@/lib/api-client";
import { formatDate } from "@/lib/i18n";
import { useTerminology } from "@/lib/terminology";
import { useSegment, type SegmentCondition, type SegmentFilterGroup } from "@/hooks/use-segments";
import { ContactTable } from "./contact-table";
import { ContactDetail } from "./contact-detail";
import { ContactFilters } from "./contact-filters";
import { BulkImport } from "./bulk-import";
import { CreateContactModal, type CreateContactData } from "@/components/crm/create-contact-modal";
import {
  CRM_PERSON_OWNER_CUSTOM_FIELD_KEYS,
  CRM_PERSON_OWNER_LABEL_CUSTOM_FIELD_KEYS,
  CRM_UNASSIGNED_ASSIGNEE_FILTER_VALUE,
} from "@ease/crm/types";

type FeedbackTone = "success" | "warning" | "error";

interface FeedbackState {
  tone: FeedbackTone;
  message: string;
}

interface ViewDraft {
  name: string;
  isShared: boolean;
}

interface ContactFilterState {
  search: string;
  tag: string;
  minScore: string;
  maxScore: string;
  createdFrom: string;
  createdTo: string;
  lifecycleStage: string;
  source: string;
  assignedTo: string;
  city: string;
  company: string;
}

const CONTACT_LIST_COLUMNS = ["contact", "score", "stage", "lastTouch", "source", "tags"];
const DEFAULT_FILTERS: ContactFilterState = {
  search: "",
  tag: "",
  minScore: "",
  maxScore: "",
  createdFrom: "",
  createdTo: "",
  lifecycleStage: "",
  source: "",
  assignedTo: "",
  city: "",
  company: "",
};

/** CRM Contacts page — list, search, filter, detail panel, bulk import. */
export default function ContactsPage() {
  const navigate = useNavigate();
  const terminology = useTerminology();
  const { contactId: routeContactId } = useParams<{ contactId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const segmentId = searchParams.get("segment");

  const [filters, setFilters] = useState<ContactFilterState>(DEFAULT_FILTERS);
  const debouncedSearch = useDebounce(filters.search);
  const debouncedTag = useDebounce(filters.tag);

  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);

  const [showImport, setShowImport] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [viewDraft, setViewDraft] = useState<ViewDraft>({ name: "", isShared: false });
  const [viewMutationError, setViewMutationError] = useState<string | null>(null);
  const [isSavingView, setIsSavingView] = useState(false);
  const [isDeletingView, setIsDeletingView] = useState(false);

  const [bulkTagInput, setBulkTagInput] = useState("");
  const [bulkFeedback, setBulkFeedback] = useState<FeedbackState | null>(null);
  const [bulkAssigneeId, setBulkAssigneeId] = useState("");
  const [isBulkTagging, setIsBulkTagging] = useState(false);
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const { segment: activeSegment } = useSegment(segmentId);
  const {
    data: organizationMembers,
    isLoading: areOrganizationMembersLoading,
    error: organizationMembersError,
  } = useOrganizationMembers();

  const { contacts, total, nextCursor, isLoading, error, loadMore, refetch } = useContactList({
    search: debouncedSearch || undefined,
    tag: debouncedTag || undefined,
    minScore: parseOptionalNumber(filters.minScore),
    maxScore: parseOptionalNumber(filters.maxScore),
    createdFrom: toStartOfDayIso(filters.createdFrom),
    createdTo: toEndOfDayIso(filters.createdTo),
    lifecycleStage: filters.lifecycleStage || undefined,
    source: filters.source || undefined,
    assignedTo: filters.assignedTo || undefined,
    city: filters.city || undefined,
    company: filters.company || undefined,
    excludeTag: debouncedTag === "shadow-person" ? undefined : "shadow-person",
    limit: 25,
  });
  const {
    sources,
    isLoading: areSourcesLoading,
    error: sourcesError,
  } = useContactSources();
  const {
    views,
    isLoading: areViewsLoading,
    error: viewsError,
    refetch: refetchViews,
  } = useSavedViews("people");

  const activeContactId = routeContactId ?? selectedContactId;
  const activeView = views.find((view) => view.id === activeViewId) ?? null;
  const appliedSegment = useMemo(() => (
    activeSegment ? mapSegmentFilters(activeSegment.filterTree) : null
  ), [activeSegment]);

  useEffect(() => {
    setSelectedContactIds((previous) => previous.filter((id) => contacts.some((contact) => contact.id === id)));
  }, [contacts]);

  useEffect(() => {
    if (!segmentId || !activeSegment || !appliedSegment) {
      return;
    }

    setFilters((current) => ({ ...current, ...appliedSegment.filters }));
    setActiveViewId(null);
    setBulkFeedback(
      appliedSegment.unsupportedCount > 0
        ? {
            tone: "warning",
            message: `${activeSegment.name} applied to the contacts list. ${appliedSegment.unsupportedCount} segment rule${appliedSegment.unsupportedCount === 1 ? " remains" : "s remain"} informational on this surface.`,
          }
        : {
            tone: "success",
            message: `${activeSegment.name} applied to the contacts list.`,
          },
    );
  }, [activeSegment, appliedSegment, segmentId]);

  const selectedContacts = useMemo(() => {
    const selectedIds = new Set(selectedContactIds);
    return contacts.filter((contact) => selectedIds.has(contact.id));
  }, [contacts, selectedContactIds]);
  const ownerOptions = useMemo(() => (
    (organizationMembers ?? []).map((member) => ({
      id: member.id,
      label: formatOrganizationMemberLabel(member),
    }))
  ), [organizationMembers]);
  const ownerLabelById = useMemo(
    () => new Map(ownerOptions.map((option) => [option.id, option.label])),
    [ownerOptions],
  );
  const bulkAssignee = (organizationMembers ?? []).find((member) => member.id === bulkAssigneeId) ?? null;
  const bulkAssigneeLabel = bulkAssignee ? formatOrganizationMemberLabel(bulkAssignee) : null;

  const handleSelectContact = useCallback((contactId: string | null) => {
    setSelectedContactId(contactId);
    navigate(contactId ? `/crm/contacts/${contactId}` : "/crm/contacts");
  }, [navigate]);

  const handleSearchChange = useCallback((value: string) => {
    setFilters((current) => ({ ...current, search: value }));
    setActiveViewId(null);
  }, []);

  const handleTagChange = useCallback((value: string) => {
    setFilters((current) => ({ ...current, tag: value }));
    setActiveViewId(null);
  }, []);

  const handleMinScoreChange = useCallback((value: string) => {
    setFilters((current) => ({ ...current, minScore: value }));
    setActiveViewId(null);
  }, []);

  const handleMaxScoreChange = useCallback((value: string) => {
    setFilters((current) => ({ ...current, maxScore: value }));
    setActiveViewId(null);
  }, []);

  const handleCreatedFromChange = useCallback((value: string) => {
    setFilters((current) => ({ ...current, createdFrom: value }));
    setActiveViewId(null);
  }, []);

  const handleCreatedToChange = useCallback((value: string) => {
    setFilters((current) => ({ ...current, createdTo: value }));
    setActiveViewId(null);
  }, []);

  const handleLifecycleStageChange = useCallback((value: string) => {
    setFilters((current) => ({ ...current, lifecycleStage: value }));
    setActiveViewId(null);
  }, []);

  const handleSourceChange = useCallback((value: string) => {
    setFilters((current) => ({ ...current, source: value }));
    setActiveViewId(null);
  }, []);

  const handleAssignedToChange = useCallback((value: string) => {
    setFilters((current) => ({ ...current, assignedTo: value }));
    setActiveViewId(null);
  }, []);

  const handleCreateContact = () => {
    setShowCreateModal(true);
  };

  const handleExportCSV = useCallback((records: ContactSummary[], prefix = "contacts") => {
    if (records.length === 0) return;
    const csv = exportContactsToCSV(records);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${prefix}-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, []);

  const handleExportAll = useCallback(async () => {
    setBulkFeedback(null);
    setIsExportingAll(true);

    try {
      const blob = await exportAllContactsCsv();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `contacts-export-${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      setBulkFeedback({
        tone: "success",
        message: "Full contact export started from the mounted server-side CSV route.",
      });
    } catch (err) {
      setBulkFeedback({
        tone: "error",
        message: err instanceof Error ? err.message : "Failed to export all contacts.",
      });
    } finally {
      setIsExportingAll(false);
    }
  }, []);

  const handleCreateSubmit = async (data: CreateContactData) => {
    setIsCreating(true);
    setCreateError(null);
    try {
      await createContact(data);
      setShowCreateModal(false);
      refetch();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create contact");
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleSelected = useCallback((contactId: string) => {
    setSelectedContactIds((previous) => (
      previous.includes(contactId)
        ? previous.filter((id) => id !== contactId)
        : [...previous, contactId]
    ));
    setBulkFeedback(null);
  }, []);

  const handleToggleAllVisible = useCallback(() => {
    setSelectedContactIds((previous) => {
      const visibleIds = contacts.map((contact) => contact.id);
      const visibleIdSet = new Set(visibleIds);
      const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => previous.includes(id));

      if (allVisibleSelected) {
        return previous.filter((id) => !visibleIdSet.has(id));
      }

      return [...new Set([...previous, ...visibleIds])];
    });
    setBulkFeedback(null);
  }, [contacts]);

  const handleClearSelection = useCallback(() => {
    setSelectedContactIds([]);
    setBulkFeedback(null);
  }, []);

  const handleApplyView = useCallback((view: SavedViewRecord) => {
    const mappedFilters = mapSavedViewFilters(view.filters);
    setFilters((current) => ({ ...current, ...mappedFilters }));
    setActiveViewId(view.id);
    setBulkFeedback(
      mappedFilters.unsupportedCount > 0
        ? {
            tone: "warning",
            message: `${view.name} applied. ${mappedFilters.unsupportedCount} saved filter${mappedFilters.unsupportedCount === 1 ? " remains" : "s remain"} unsupported on the contacts surface.`,
          }
        : {
            tone: "success",
            message: `${view.name} applied to the contacts list.`,
          },
    );
  }, []);

  const handleSaveView = useCallback(async () => {
    const trimmedName = viewDraft.name.trim();
    if (!trimmedName) {
      setViewMutationError("Name the view before saving it.");
      return;
    }

    setIsSavingView(true);
    setViewMutationError(null);

    try {
      const savedView = await createSavedView({
        name: trimmedName,
        entityType: "people",
        filters: buildSavedViewFilters(filters),
        columns: CONTACT_LIST_COLUMNS,
        isShared: viewDraft.isShared,
      });
      setViewDraft({ name: "", isShared: false });
      setActiveViewId(savedView.id);
      setBulkFeedback({
        tone: "success",
        message: viewDraft.isShared
          ? `${savedView.name} is now visible to your team.`
          : `${savedView.name} saved for your personal workflow.`,
      });
      await refetchViews();
    } catch (err) {
      setViewMutationError(err instanceof ApiError ? err.message : "Failed to save this view");
    } finally {
      setIsSavingView(false);
    }
  }, [filters, refetchViews, viewDraft]);

  const handleDeleteView = useCallback(async () => {
    if (!activeView) return;

    setIsDeletingView(true);
    setViewMutationError(null);

    try {
      await deleteSavedView(activeView.id);
      setActiveViewId(null);
      setViewDraft({ name: "", isShared: false });
      setBulkFeedback({
        tone: "success",
        message: `${activeView.name} removed from saved views.`,
      });
      await refetchViews();
    } catch (err) {
      setViewMutationError(err instanceof ApiError ? err.message : "Failed to delete this saved view");
    } finally {
      setIsDeletingView(false);
    }
  }, [activeView, refetchViews]);

  const handleBulkTag = useCallback(async () => {
    const trimmedTag = bulkTagInput.trim();
    if (!trimmedTag) {
      setBulkFeedback({ tone: "error", message: "Enter a tag before applying a bulk update." });
      return;
    }

    setIsBulkTagging(true);
    setBulkFeedback(null);

    try {
      const results = await Promise.allSettled(
        selectedContactIds.map((id) => addTagsToContact(id, [trimmedTag])),
      );
      const succeeded = results.filter((result) => result.status === "fulfilled").length;
      const failed = results.length - succeeded;

      if (succeeded > 0) {
        refetch();
        setBulkTagInput("");
      }

      setBulkFeedback(summarizeBulkResult({
        actionLabel: "tag update",
        succeeded,
        failed,
      }));
    } finally {
      setIsBulkTagging(false);
    }
  }, [bulkTagInput, refetch, selectedContactIds]);

  const handleBulkAssign = useCallback(async () => {
    if (!bulkAssigneeId || !bulkAssigneeLabel) {
      setBulkFeedback({ tone: "error", message: "Choose an owner before assigning selected contacts." });
      return;
    }

    setIsBulkAssigning(true);
    setBulkFeedback(null);

    try {
      const results = await Promise.allSettled(
        selectedContactIds.map((id) => assignContactOwner(id, bulkAssigneeId, bulkAssigneeLabel)),
      );
      const succeeded = results.filter((result) => result.status === "fulfilled").length;
      const failed = results.length - succeeded;

      if (succeeded > 0) {
        refetch();
      }

      setBulkFeedback(summarizeBulkResult({
        actionLabel: "assignment",
        succeeded,
        failed,
      }));
    } finally {
      setIsBulkAssigning(false);
    }
  }, [bulkAssigneeId, bulkAssigneeLabel, refetch, selectedContactIds]);

  const handleBulkUnassign = useCallback(async () => {
    setIsBulkAssigning(true);
    setBulkFeedback(null);

    try {
      const results = await Promise.allSettled(
        selectedContactIds.map((id) => clearContactOwner(id)),
      );
      const succeeded = results.filter((result) => result.status === "fulfilled").length;
      const failed = results.length - succeeded;

      if (succeeded > 0) {
        refetch();
        setBulkAssigneeId("");
      }

      setBulkFeedback(summarizeBulkResult({
        actionLabel: "owner removal",
        succeeded,
        failed,
      }));
    } finally {
      setIsBulkAssigning(false);
    }
  }, [refetch, selectedContactIds]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedContactIds.length === 0) {
      return;
    }

    setIsBulkDeleting(true);
    setBulkFeedback(null);

    try {
      const deletedIds = [...selectedContactIds];
      const results = await Promise.allSettled(deletedIds.map((id) => deleteContactRecord(id)));
      const succeededIds = results.flatMap((result, index) => (
        result.status === "fulfilled" ? [deletedIds[index]] : []
      ));
      const failed = results.length - succeededIds.length;

      if (succeededIds.length > 0) {
        setSelectedContactIds((previous) => previous.filter((id) => !succeededIds.includes(id)));
        if (activeContactId && succeededIds.includes(activeContactId)) {
          void handleSelectContact(null);
        }
        refetch();
      }

      setBulkFeedback(summarizeBulkResult({
        actionLabel: "delete",
        succeeded: succeededIds.length,
        failed,
      }));
    } finally {
      setIsBulkDeleting(false);
    }
  }, [activeContactId, handleSelectContact, refetch, selectedContactIds]);

  const isAllVisibleSelected = contacts.length > 0 && contacts.every((contact) => selectedContactIds.includes(contact.id));

  return (
    <div className="density-gap space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{terminology.contacts}</h1>
          {total > 0 && (
            <p className="text-sm text-muted-foreground">{total} contacts</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {segmentId && activeSegment && (
            <button
              type="button"
              onClick={() => {
                const nextParams = new URLSearchParams(searchParams);
                nextParams.delete("segment");
                setSearchParams(nextParams);
                setBulkFeedback({
                  tone: "success",
                  message: `${activeSegment.name} detached from the contacts handoff. Your current filter edits stay in place.`,
                });
              }}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-muted-foreground)] transition-colors hover:bg-[var(--color-surface)]"
            >
              <Filter className="h-3.5 w-3.5" /> Clear segment handoff
            </button>
          )}
          <Link
            to="/crm/duplicates"
            className="inline-flex min-h-[44px] items-center rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-muted-foreground)] transition-colors hover:bg-[var(--color-surface)]"
          >
            Duplicate Review
          </Link>
          {contacts.length > 0 && (
            <button
              onClick={() => { void handleExportAll(); }}
              disabled={isExportingAll}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-muted-foreground)] transition-colors hover:bg-[var(--color-surface)]"
            >
              {isExportingAll ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Export All
            </button>
          )}
          <button
            onClick={() => setShowImport(true)}
            className="inline-flex min-h-[44px] items-center rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-muted-foreground)] transition-colors hover:bg-[var(--color-surface)]"
          >
            Import CSV
          </button>
        </div>
      </div>

      {error && (
        <InlineBanner tone="error">
          <span>{error}</span>
          <button onClick={refetch} className="min-h-[44px] min-w-[44px] px-2 py-2 underline">
            Retry
          </button>
        </InlineBanner>
      )}

      {createError && (
        <InlineBanner tone="error">
          <span>{createError}</span>
          <button onClick={() => setCreateError(null)} className="min-h-[44px] min-w-[44px] px-2 py-2 underline">
            Dismiss
          </button>
        </InlineBanner>
      )}

      {bulkFeedback && (
        <InlineBanner tone={bulkFeedback.tone}>
          <span>{bulkFeedback.message}</span>
          <button onClick={() => setBulkFeedback(null)} className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1 px-2 py-2 text-xs font-medium">
            <X className="h-3 w-3" /> Dismiss
          </button>
        </InlineBanner>
      )}

      <div className="grid gap-4 xl:grid-cols-[18rem,minmax(0,1fr)]">
        <aside className="space-y-4">
          <section aria-label="Saved views" className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]/70 p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">
                  Saved views
                </p>
                <h2 className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
                  Keep your sharpest filters close
                </h2>
              </div>
              <div className="rounded-full border border-[var(--color-border)] px-2.5 py-1 text-xs font-medium text-[var(--color-muted-foreground)]">
                {views.length}
              </div>
            </div>

            <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
              Reuse the exact search and tag combinations that keep the contact queue moving.
            </p>

            <div className="mt-4 space-y-3 rounded-xl border border-dashed border-[var(--color-border)] bg-background/70 p-3">
              <label className="space-y-1.5">
                <span className="text-xs font-medium text-[var(--color-muted-foreground)]">View name</span>
                <input
                  type="text"
                  value={viewDraft.name}
                  onChange={(event) => setViewDraft((current) => ({ ...current, name: event.target.value }))}
                  placeholder="High-intent VIP leads"
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </label>
              <label className="flex min-h-[44px] items-center gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)]">
                <input
                  type="checkbox"
                  checked={viewDraft.isShared}
                  onChange={(event) => setViewDraft((current) => ({ ...current, isShared: event.target.checked }))}
                  aria-label="Share this view with the team"
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary/20"
                />
                <span className="inline-flex items-center gap-2">
                  <Users className="h-4 w-4 text-[var(--color-muted-foreground)]" />
                  Share with the team
                </span>
              </label>
              <button
                type="button"
                onClick={handleSaveView}
                disabled={isSavingView}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isSavingView ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save current filters
              </button>
              {viewMutationError && (
                <p className="inline-flex items-center gap-2 text-xs text-destructive">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {viewMutationError}
                </p>
              )}
            </div>

            <div className="mt-4 space-y-2">
              <button
                type="button"
                onClick={() => {
                  setFilters(DEFAULT_FILTERS);
                  setActiveViewId(null);
                }}
                className="inline-flex min-h-[44px] items-center gap-2 text-sm font-medium text-[var(--color-muted-foreground)] transition-colors hover:text-[var(--color-foreground)]"
              >
                <Filter className="h-4 w-4" />
                Reset to all contacts
              </button>

              {areViewsLoading ? (
                <div aria-busy="true" aria-label="Loading saved views" className="space-y-2 pt-2">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton key={index} className="h-14 rounded-xl" />
                  ))}
                </div>
              ) : viewsError ? (
                <div role="alert" className="rounded-xl border border-destructive/20 bg-destructive/5 p-3 text-sm text-destructive">
                  <p>{viewsError}</p>
                  <button onClick={refetchViews} className="mt-2 min-h-[44px] min-w-[44px] px-2 py-2 underline">
                    Retry saved views
                  </button>
                </div>
              ) : views.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[var(--color-border)] p-4 text-sm text-[var(--color-muted-foreground)]">
                  Save a filter set to keep repeat work out of the search bar.
                </div>
              ) : (
                <div className="space-y-2">
                  {views.map((view) => {
                    const isActive = activeViewId === view.id;
                    return (
                      <button
                        key={view.id}
                        type="button"
                        onClick={() => handleApplyView(view)}
                        aria-current={isActive ? "true" : undefined}
                        className={`stagger-item w-full rounded-xl border px-3 py-3 text-left transition-colors ${
                          isActive
                            ? "border-[var(--color-primary)] bg-[var(--color-primary)]/8"
                            : "border-[var(--color-border)] bg-background hover:bg-[var(--color-surface)]"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-[var(--color-foreground)]">{view.name}</p>
                            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                              {describeSavedView(view.filters, ownerLabelById)}
                            </p>
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                            {view.isShared && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-primary)]/10 px-2 py-0.5 text-[11px] font-medium text-[var(--color-primary)]">
                                <Share2 className="h-3 w-3" /> Shared
                              </span>
                            )}
                            {isActive && <Check className="h-4 w-4 text-[var(--color-primary)]" />}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {activeView && (
              <button
                type="button"
                onClick={handleDeleteView}
                disabled={isDeletingView}
                aria-label={activeView ? `Delete saved view: ${activeView.name}` : "Delete active view"}
                className="mt-4 inline-flex min-h-[44px] items-center gap-2 text-sm font-medium text-[var(--color-muted-foreground)] transition-colors hover:text-destructive disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isDeletingView ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Delete active view
              </button>
            )}
          </section>
        </aside>

        <div className="space-y-4">
          <ContactFilters
            search={filters.search}
            onSearchChange={handleSearchChange}
            tag={filters.tag}
            onTagChange={handleTagChange}
            minScore={filters.minScore}
            onMinScoreChange={handleMinScoreChange}
            maxScore={filters.maxScore}
            onMaxScoreChange={handleMaxScoreChange}
            createdFrom={filters.createdFrom}
            onCreatedFromChange={handleCreatedFromChange}
            createdTo={filters.createdTo}
            onCreatedToChange={handleCreatedToChange}
            lifecycleStage={filters.lifecycleStage}
          onLifecycleStageChange={handleLifecycleStageChange}
          source={filters.source}
          onSourceChange={handleSourceChange}
          sourceOptions={sources}
          isSourceOptionsLoading={areSourcesLoading}
          sourceOptionsError={sourcesError}
          assignedTo={filters.assignedTo}
          onAssignedToChange={handleAssignedToChange}
          ownerOptions={ownerOptions}
          areOwnerOptionsLoading={areOrganizationMembersLoading}
          ownerOptionsError={organizationMembersError}
          onCreateContact={handleCreateContact}
        />

          {selectedContactIds.length > 0 && (
            <section aria-label={`Bulk actions for ${selectedContactIds.length} selected contacts`} className="rounded-2xl border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/6 p-4 shadow-sm">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">
                    Bulk mode
                  </p>
                  <h3 className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
                    {selectedContactIds.length} contact{selectedContactIds.length === 1 ? "" : "s"} selected
                  </h3>
                  <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                    Tag, export, assign, unassign, or delete the visible selection using mounted CRM contracts.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleClearSelection}
                  className="inline-flex min-h-[44px] items-center gap-2 text-sm font-medium text-[var(--color-muted-foreground)] transition-colors hover:text-[var(--color-foreground)]"
                >
                  <X className="h-4 w-4" />
                  Clear selection
                </button>
              </div>

              <div className="mt-4 grid gap-3 xl:grid-cols-[minmax(0,1fr),auto,auto,auto]">
                <div className="flex flex-col gap-2 sm:flex-row">
                  <div className="relative flex-1">
                    <Tags className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
                    <input
                      type="text"
                      value={bulkTagInput}
                      onChange={(event) => setBulkTagInput(event.target.value)}
                      placeholder="Tag the current selection"
                      className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleBulkTag}
                    disabled={isBulkTagging}
                    className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-surface)] disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isBulkTagging ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Tags className="h-4 w-4" />}
                    Add tag
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => handleExportCSV(selectedContacts, "selected-contacts")}
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-surface)]"
                >
                  <Download className="h-4 w-4" />
                  Export selected
                </button>

                <div className="flex flex-col gap-1">
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <select
                      aria-label="Bulk assignee"
                      value={bulkAssigneeId}
                      onChange={(event) => {
                        setBulkAssigneeId(event.target.value);
                        setBulkFeedback(null);
                      }}
                      disabled={areOrganizationMembersLoading || isBulkAssigning || (organizationMembers?.length ?? 0) === 0}
                      className="min-h-[44px] rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <option value="">
                        {areOrganizationMembersLoading
                          ? "Loading owners..."
                          : (organizationMembers?.length ?? 0) > 0
                            ? "Assign owner"
                            : "No owners available"}
                      </option>
                      {(organizationMembers ?? []).map((member) => (
                        <option key={member.id} value={member.id}>
                          {formatOrganizationMemberLabel(member)}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleBulkAssign}
                      disabled={!bulkAssigneeId || isBulkAssigning || areOrganizationMembersLoading}
                      className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-surface)] disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {isBulkAssigning ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}
                      Assign selected
                    </button>
                    <button
                      type="button"
                      onClick={handleBulkUnassign}
                      disabled={isBulkAssigning}
                      className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-surface)] disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {isBulkAssigning ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <UserX className="h-4 w-4" />}
                      Unassign selected
                    </button>
                  </div>
                  {organizationMembersError && (
                    <p className="text-xs text-[var(--color-muted-foreground)]">
                      Owner options are temporarily unavailable.
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleBulkDelete}
                  disabled={isBulkDeleting}
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md border border-destructive/25 bg-destructive/5 px-4 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isBulkDeleting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  Delete selected
                </button>
              </div>
            </section>
          )}

          {!isLoading && contacts.length === 0 && !error ? (
            <EmptyState
              icon={<Users className="h-8 w-8" />}
              tier={2}
              eyebrow={`${terminology.contacts} setup`}
              title={`No ${terminology.contacts.toLowerCase()} yet`}
              description={`Bring in your first ${terminology.contacts.toLowerCase()} so segments, saved views, and outreach start from live records instead of an empty table.`}
              actionLabel={`Add ${terminology.contact.toLowerCase()}`}
              onAction={handleCreateContact}
              secondaryLabel={`Import ${terminology.contacts.toLowerCase()}`}
              onSecondary={() => setShowImport(true)}
              preview={(
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="h-3 w-24 rounded-full bg-[var(--color-muted)]/80" />
                    <div className="h-3 w-12 rounded-full bg-[var(--color-muted)]/60" />
                  </div>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div key={index} className="flex items-center justify-between rounded-xl border border-[var(--color-border)] bg-[var(--color-background)]/80 px-3 py-2 opacity-70 blur-[1px]">
                      <div className="space-y-2">
                        <div className="h-3 w-28 rounded-full bg-[var(--color-muted)]/80" />
                        <div className="h-2.5 w-20 rounded-full bg-[var(--color-muted)]/60" />
                      </div>
                      <div className="h-5 w-14 rounded-full bg-[var(--color-primary)]/15" />
                    </div>
                  ))}
                </div>
              )}
              checklistItems={[
                `Add one ${terminology.contact.toLowerCase()} manually or import a CSV export from your old system.`,
                "Tag your highest-intent relationships so your first saved segment is meaningful.",
                "Load sample data from onboarding if you need a safe sandbox before inviting the team.",
              ]}
              aiHint="Import my contacts from a CSV"
            />
          ) : (
            <>
              <ContactTable
                contacts={contacts}
                selectedId={activeContactId}
                selectedContactIds={selectedContactIds}
                ownerLabelById={ownerLabelById}
                onSelect={handleSelectContact}
                onToggleSelected={handleToggleSelected}
                onToggleAllVisible={handleToggleAllVisible}
                isAllVisibleSelected={isAllVisibleSelected}
                isLoading={isLoading}
              />

              {nextCursor && (
                <div className="flex justify-center py-4">
                  <button
                    onClick={loadMore}
                    className="min-h-[44px] rounded-md border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-accent"
                  >
                    Load more
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {activeContactId && (
        <ContactDetail
          contactId={activeContactId}
          onClose={() => { void handleSelectContact(null); }}
          onDeleted={() => { void handleSelectContact(null); refetch(); }}
        />
      )}

      {showImport && (
        <BulkImport
          onClose={() => setShowImport(false)}
          onComplete={refetch}
        />
      )}

      {showCreateModal && (
        <CreateContactModal
          onSubmit={handleCreateSubmit}
          onClose={() => setShowCreateModal(false)}
          isSubmitting={isCreating}
        />
      )}
    </div>
  );
}

function InlineBanner({
  children,
  tone,
}: {
  children: ReactNode;
  tone: FeedbackTone;
}) {
  const toneClasses = {
    success: "border-emerald-500/20 bg-emerald-500/6 text-emerald-700 dark:text-emerald-300",
    warning: "border-amber-500/20 bg-amber-500/6 text-amber-700 dark:text-amber-300",
    error: "border-destructive/20 bg-destructive/5 text-destructive",
  };

  return (
    <div role={tone === "error" ? "alert" : "status"} className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm ${toneClasses[tone]}`}>
      <div className="flex items-center gap-2">
        <AlertCircle className="h-4 w-4" aria-hidden="true" />
        {children}
      </div>
    </div>
  );
}

function buildSavedViewFilters(filters: ContactFilterState): SavedViewFilter[] {
  const viewFilters: SavedViewFilter[] = [];

  if (filters.search.trim()) {
    viewFilters.push({
      field: "search",
      operator: "contains",
      value: filters.search.trim(),
    });
  }

  if (filters.tag.trim()) {
    viewFilters.push({
      field: "tag",
      operator: "contains",
      value: filters.tag.trim(),
    });
  }

  if (filters.minScore.trim()) {
    viewFilters.push({
      field: "minScore",
      operator: "gte",
      value: Number(filters.minScore),
    });
  }

  if (filters.maxScore.trim()) {
    viewFilters.push({
      field: "maxScore",
      operator: "lte",
      value: Number(filters.maxScore),
    });
  }

  if (filters.createdFrom) {
    viewFilters.push({
      field: "createdFrom",
      operator: "gte",
      value: toStartOfDayIso(filters.createdFrom),
    });
  }

  if (filters.createdTo) {
    viewFilters.push({
      field: "createdTo",
      operator: "lte",
      value: toEndOfDayIso(filters.createdTo),
    });
  }

  if (filters.lifecycleStage) {
    viewFilters.push({
      field: "lifecycleStage",
      operator: "eq",
      value: filters.lifecycleStage,
    });
  }

  if (filters.source) {
    viewFilters.push({
      field: "source",
      operator: "eq",
      value: filters.source,
    });
  }

  if (filters.assignedTo) {
    viewFilters.push({
      field: "assignedTo",
      operator: "eq",
      value: filters.assignedTo,
    });
  }

  if (filters.city) {
    viewFilters.push({
      field: "city",
      operator: "contains",
      value: filters.city,
    });
  }

  if (filters.company) {
    viewFilters.push({
      field: "company",
      operator: "contains",
      value: filters.company,
    });
  }

  return viewFilters;
}

function mapSavedViewFilters(filters: SavedViewFilter[]) {
  const mapped: ContactFilterState = { ...DEFAULT_FILTERS };
  let unsupportedCount = 0;

  for (const filter of filters) {
    if (filter.field === "search" && typeof filter.value === "string") {
      mapped.search = filter.value;
      continue;
    }
    if (filter.field === "tag" && typeof filter.value === "string") {
      mapped.tag = filter.value;
      continue;
    }
    if (filter.field === "minScore" && typeof filter.value === "number") {
      mapped.minScore = String(filter.value);
      continue;
    }
    if (filter.field === "maxScore" && typeof filter.value === "number") {
      mapped.maxScore = String(filter.value);
      continue;
    }
    if (filter.field === "createdFrom" && typeof filter.value === "string") {
      mapped.createdFrom = filter.value.slice(0, 10);
      continue;
    }
    if (filter.field === "createdTo" && typeof filter.value === "string") {
      mapped.createdTo = filter.value.slice(0, 10);
      continue;
    }
    if (filter.field === "lifecycleStage" && typeof filter.value === "string") {
      mapped.lifecycleStage = filter.value;
      continue;
    }
    if (filter.field === "source" && typeof filter.value === "string") {
      mapped.source = filter.value;
      continue;
    }
    if (filter.field === "assignedTo" && typeof filter.value === "string") {
      mapped.assignedTo = filter.value;
      continue;
    }
    if (filter.field === "city" && typeof filter.value === "string") {
      mapped.city = filter.value;
      continue;
    }
    if (filter.field === "company" && typeof filter.value === "string") {
      mapped.company = filter.value;
      continue;
    }
    unsupportedCount += 1;
  }

  return { ...mapped, unsupportedCount };
}

function describeSavedView(filters: SavedViewFilter[], ownerLabelById: Map<string, string>): string {
  const mapped = mapSavedViewFilters(filters);
  const parts = [
    mapped.search ? `Search: ${mapped.search}` : null,
    mapped.tag ? `Tag: ${mapped.tag}` : null,
    mapped.minScore ? `Min score: ${mapped.minScore}` : null,
    mapped.maxScore ? `Max score: ${mapped.maxScore}` : null,
    mapped.lifecycleStage ? `Stage: ${mapped.lifecycleStage}` : null,
    mapped.source ? `Source: ${mapped.source}` : null,
    mapped.assignedTo
      ? `Owner: ${formatSavedViewOwner(mapped.assignedTo, ownerLabelById)}`
      : null,
    mapped.city ? `City: ${mapped.city}` : null,
    mapped.company ? `Company: ${mapped.company}` : null,
  ].filter(Boolean);

  if (mapped.unsupportedCount > 0) {
    parts.push(`${mapped.unsupportedCount} advanced filter${mapped.unsupportedCount === 1 ? "" : "s"}`);
  }

  return parts.length > 0 ? parts.join(" • ") : "All contacts";
}

function formatSavedViewOwner(ownerId: string, ownerLabelById: Map<string, string>): string {
  if (ownerId === CRM_UNASSIGNED_ASSIGNEE_FILTER_VALUE) {
    return "Unassigned";
  }
  return ownerLabelById.get(ownerId) ?? ownerId;
}

function summarizeBulkResult({
  actionLabel,
  succeeded,
  failed,
}: {
  actionLabel: string;
  succeeded: number;
  failed: number;
}): FeedbackState {
  if (succeeded > 0 && failed === 0) {
    return {
      tone: "success",
      message: `${capitalize(actionLabel)} completed for ${succeeded} contact${succeeded === 1 ? "" : "s"}.`,
    };
  }

  if (succeeded > 0 && failed > 0) {
    return {
      tone: "warning",
      message: `${capitalize(actionLabel)} completed for ${succeeded} contact${succeeded === 1 ? "" : "s"}, but ${failed} update${failed === 1 ? "" : "s"} failed.`,
    };
  }

  return {
    tone: "error",
    message: `${capitalize(actionLabel)} failed. No selected contacts were updated.`,
  };
}

function capitalize(value: string): string {
  return value.length > 0 ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

function parseOptionalNumber(value: string): number | undefined {
  if (!value.trim()) {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toStartOfDayIso(value: string): string | undefined {
  return value ? `${value}T00:00:00.000Z` : undefined;
}

function toEndOfDayIso(value: string): string | undefined {
  return value ? `${value}T23:59:59.999Z` : undefined;
}

async function addTagsToContact(contactId: string, tags: string[]): Promise<void> {
  await apiFetch(`/crm/people/${contactId}/tags`, {
    method: "POST",
    body: JSON.stringify({ tags }),
  });
}

async function deleteContactRecord(contactId: string): Promise<void> {
  await apiFetch(`/crm/people/${contactId}`, { method: "DELETE" });
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function formatOrganizationMemberLabel(member: { name: string | null; email: string }): string {
  return member.name?.trim() || member.email;
}

function buildContactOwnerCustomFields(
  currentCustomFields: Record<string, unknown>,
  owner: { assigneeId: string; assigneeLabel: string } | null,
): Record<string, unknown> {
  const nextCustomFields = { ...currentCustomFields };

  for (const key of CRM_PERSON_OWNER_CUSTOM_FIELD_KEYS) {
    delete nextCustomFields[key];
  }
  for (const key of CRM_PERSON_OWNER_LABEL_CUSTOM_FIELD_KEYS) {
    delete nextCustomFields[key];
  }

  if (owner) {
    nextCustomFields.assignedTo = owner.assigneeId;
    nextCustomFields.assignedToLabel = owner.assigneeLabel;
  }

  return nextCustomFields;
}

async function updateContactOwner(
  contactId: string,
  owner: { assigneeId: string; assigneeLabel: string } | null,
): Promise<void> {
  const response = await apiFetch<{ data: { customFields?: Record<string, unknown> | null } }>(`/crm/people/${contactId}`);
  const currentCustomFields = isPlainRecord(response.data.customFields) ? response.data.customFields : {};

  await apiFetch(`/crm/people/${contactId}`, {
    method: "PATCH",
    body: JSON.stringify({
      customFields: buildContactOwnerCustomFields(currentCustomFields, owner),
    }),
  });
}

async function assignContactOwner(contactId: string, assigneeId: string, assigneeLabel: string): Promise<void> {
  await updateContactOwner(contactId, { assigneeId, assigneeLabel });
}

async function clearContactOwner(contactId: string): Promise<void> {
  await updateContactOwner(contactId, null);
}

export function exportContactsToCSV(contacts: ContactSummary[]): string {
  const headers = ["Name", "Email", "Phone", "Company", "Source", "Score", "Stage", "Tags", "Created"];
  const rows = contacts.map((c) => [
    [c.firstName, c.lastName].filter(Boolean).join(" ") || "Unknown",
    c.email ?? "",
    c.phone ?? "",
    c.company ?? "",
    c.source ?? "",
    String(c.leadScore ?? ""),
    c.lifecycleStage ?? "",
    c.tags.join("; "),
    c.createdAt ? formatDate(c.createdAt) : "",
  ]);

  const escape = (val: string) => {
    if (val.includes(",") || val.includes('"') || val.includes("\n")) {
      return `"${val.replace(/"/g, '""')}"`;
    }
    return val;
  };

  return [
    headers.join(","),
    ...rows.map((row) => row.map(escape).join(",")),
  ].join("\n");
}

function mapSegmentFilters(filterTree: SegmentFilterGroup | null) {
  const mapped: ContactFilterState = { ...DEFAULT_FILTERS };
  let unsupportedCount = 0;

  if (!filterTree) {
    return { filters: mapped, unsupportedCount };
  }

  const walk = (group: SegmentFilterGroup) => {
    for (const condition of group.conditions) {
      if ("operator" in condition) {
        walk(condition);
        continue;
      }

      if (applySegmentCondition(mapped, condition)) {
        continue;
      }

      unsupportedCount += 1;
    }
  };

  walk(filterTree);
  return { filters: mapped, unsupportedCount };
}

function applySegmentCondition(filters: ContactFilterState, condition: SegmentCondition): boolean {
  if (condition.field === "score" && (condition.op === "gte" || condition.op === "gt") && typeof condition.value === "number") {
    filters.minScore = String(condition.value);
    return true;
  }

  if (condition.field === "score" && (condition.op === "lte" || condition.op === "lt") && typeof condition.value === "number") {
    filters.maxScore = String(condition.value);
    return true;
  }

  if (condition.field === "lifecycleStage" && condition.op === "eq" && typeof condition.value === "string") {
    filters.lifecycleStage = condition.value;
    return true;
  }

  if (condition.field === "source" && condition.op === "eq" && typeof condition.value === "string") {
    filters.source = condition.value;
    return true;
  }

  if (condition.field === "tag" && (condition.op === "contains" || condition.op === "eq") && typeof condition.value === "string") {
    filters.tag = condition.value;
    return true;
  }

  if (condition.field === "createdAt" && condition.op === "gte" && typeof condition.value === "string") {
    filters.createdFrom = condition.value.slice(0, 10);
    return true;
  }

  if (condition.field === "createdAt" && condition.op === "lte" && typeof condition.value === "string") {
    filters.createdTo = condition.value.slice(0, 10);
    return true;
  }

  if (condition.field === "createdAt" && condition.op === "between" && Array.isArray(condition.value)) {
    const [start, end] = condition.value;
    if (typeof start === "string") {
      filters.createdFrom = start.slice(0, 10);
    }
    if (typeof end === "string") {
      filters.createdTo = end.slice(0, 10);
    }
    return true;
  }

  if (
    condition.field === "custom.city"
    && (condition.op === "eq" || condition.op === "contains" || condition.op === "starts_with")
    && typeof condition.value === "string"
  ) {
    filters.city = condition.value;
    return true;
  }

  if (
    condition.field === "custom.company"
    && (condition.op === "eq" || condition.op === "contains" || condition.op === "starts_with")
    && typeof condition.value === "string"
  ) {
    filters.company = condition.value;
    return true;
  }

  return false;
}
